import * as React from 'react'
import appCss from '../app.css?url'
import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  retainSearchParams,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
// import * as Sentry from '@sentry/react'
import { useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { onToasterActivated, flushPendingToasts } from '@/lib/toast-signal'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import * as v from 'valibot'
import { Profile } from '@/lib/profile-search-schema'
import { useAppStore } from '@/lib/app-store'
import { accentThemeVars } from '@/lib/accent-theme'
import { useCookies } from 'react-cookie'
import { CookieBanner } from './_app/-components/shared/cookie-banner'
import { type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { FormOptionalLabelProvider } from '@/components/custom/form/contexts'
import { resolveServerTheme } from '@/lib/server-theme'
import { assetUrl, getRuntimeCdnUrl } from '@/lib/asset-url'
import type { Locale } from 'shared/locales'
import { trpc, clearQueryClient, PERSISTED_IDENTITY_KEY } from '@/main'

// The Toaster (and the whole sonner package) is fetched only after the first
// toast() call — see lib/toast.ts. OnDemandToaster listens for that activation,
// then mounts the lazy <Toaster> and replays any queued toasts.
const Toaster = React.lazy(() => import('@/components/ui/sonner').then((m) => ({ default: m.Toaster })))

function OnDemandToaster() {
  const [show, setShow] = React.useState(false)
  React.useEffect(() => onToasterActivated(() => setShow(true)), [])
  if (!show) return null
  return (
    <React.Suspense fallback={null}>
      <ToasterWithFlush />
    </React.Suspense>
  )
}

function ToasterWithFlush() {
  React.useEffect(() => {
    flushPendingToasts()
  }, [])
  return (
    <Toaster
      className="whitespace-pre-line print:hidden"
      offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
      mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
      position="top-center"
      theme="light"
    />
  )
}

const defaultValues = {
  // Search & Filters
  q: '',
  p: 0,
  // Teams list filters
  teamsSort: 'name',
  // Report
  pageType: 'detailed',
  reportSort: 'index',
  // Distribution
  distSort: 'index',
  distPosition: 'highs',
  // Board
  boardType: 'competences',
  boardSort: 'index',
  boardAxis: 'x',
  // Orgs
  orgType: 'basic',
  // Profiles
  selected: 'first',
  type: 'competences',
  // TODO: this should not be necessary, check
  evaluation: 'owns',
} as const

const EvaluationV = v.picklist(['owns', 'ownsManual', 'team', 'manual'])

const rootSearchSchema = v.object({
  // Search & Filters
  q: v.optional(v.string(), defaultValues.q),
  p: v.optional(v.pipe(v.number(), v.minValue(0)), defaultValues.p),
  o: v.fallback(v.optional(v.picklist(['adjust', 'global'])), undefined),
  od: v.fallback(v.optional(v.picklist(['asc', 'desc'])), undefined),
  // Offers list filters
  offerSort: v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  // Jobs list filters
  jobSort: v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  // Teams list filters
  teamsSort: v.fallback(v.optional(v.picklist(['name', 'size']), defaultValues.teamsSort), defaultValues.teamsSort),
  // Report
  pageType: v.fallback(
    v.optional(v.picklist(['detailed', 'resume']), defaultValues['pageType']),
    defaultValues['pageType'],
  ),
  reportSort: v.fallback(
    v.optional(v.picklist(['index', 'desc']), defaultValues['reportSort']),
    defaultValues['reportSort'],
  ),
  // Distribution
  distSort: v.fallback(v.optional(v.picklist(['desc', 'index']), defaultValues['distSort']), defaultValues['distSort']),
  distPosition: v.fallback(
    v.optional(v.picklist(['highs', /* 'medium', */ 'lows']), defaultValues['distPosition']),
    defaultValues['distPosition'],
  ),
  // Board
  boardType: v.fallback(
    v.optional(v.picklist(['competences', 'vectors', 'dimensions']), defaultValues['boardType']),
    defaultValues['boardType'],
  ),
  boardSort: v.fallback(
    v.optional(v.picklist(['index', 'desc', 'asc']), defaultValues['boardSort']),
    defaultValues['boardSort'],
  ),
  boardId: v.optional(v.union([v.number(), v.string()])), // competence | vector | dimension | member
  boardAxis: v.fallback(v.optional(v.picklist(['x', 'y']), defaultValues['boardAxis']), defaultValues['boardAxis']),
  boardPosition: v.optional(v.number()),
  // Orgs
  orgType: v.fallback(
    v.optional(v.picklist(['basic', 'professional', 'transversal', 'directives']), defaultValues['orgType']),
    defaultValues['orgType'],
  ),
  // Profiles
  first: v.optional(Profile),
  second: v.optional(Profile),
  selected: v.fallback(
    v.optional(v.picklist(['first', 'second', 'compare']), defaultValues['selected']),
    defaultValues['selected'],
  ),
  type: v.fallback(
    v.optional(v.picklist(['competences', 'values', 'personality']), defaultValues.type),
    defaultValues.type,
  ),
  // TeamProfileEvaluation fields (was `...TeamProfileEvaluation.shape`)
  // TODO: this should not be necessary, check
  teamId: v.optional(v.number()),
  evaluation: v.fallback(v.optional(EvaluationV), undefined),
  members: v.optional(v.array(v.number())),
  name: v.nullish(v.string()),
  // Form dialogs (opened via search params: number = edit by id, 'new' = create)
  editEmployee: v.optional(v.union([v.number(), v.literal('new')])),
  editTeam: v.optional(v.union([v.number(), v.literal('new')])),
  editOffer: v.optional(v.union([v.number(), v.literal('new')])),
  editJob: v.optional(v.union([v.number(), v.literal('new')])),
  // SDK / API token-link params. The public API hands out links like `/report?token=…&lang=…`;
  // the token is the session (sent as the Bearer header on every request, see main.tsx) and `lang`
  // drives i18n. They live at the root + are kept by `retainSearchParams` below so in-app navigation
  // (e.g. register → /test → /report) never strips them and drops the token-bearer out of the flow.
  token: v.optional(v.string()),
  lang: v.optional(v.string()),
  clientId: v.optional(v.string()),
  ref: v.optional(v.string()),
})

// The resolved root search shape. Routes that retain all search params type
// `retainSearchParams<RootSearch>(true)` with it — without the explicit type argument
// the generic falls back to `object`, which doesn't satisfy the route's search type.
export type RootSearch = v.InferOutput<typeof rootSearchSchema>

// Public per-deployment config (social/Stripe/Sentry ids, APP_ENV) the legacy
// index.html used to inject. These come from SST runtime secrets, which only
// exist server-side — the CI build (and thus import.meta.env / Vite `define`)
// never sees them — so read process.env during SSR and bake the values in here.
// `process` is undefined in the browser; the bundle then ships the empty
// fallbacks, and the `|| value` form makes the client re-render a no-op clobber
// (the server-rendered real value already won, so it survives hydration).
// USER/APP stay null by design: the SPA shell is auth-unaware and cacheable, so
// the app resolves the user/app client-side (see auth.getUser / app-store).
const publicEnv =
  typeof process !== 'undefined'
    ? {
        GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID ?? '',
        FACEBOOK_CLIENT_ID: process.env.FACEBOOK_CLIENT_ID ?? '',
        LINKEDIN_CLIENT_ID: process.env.LINKEDIN_CLIENT_ID ?? '',
        STRIPE_PUBLIC_KEY: process.env.STRIPE_PUBLIC_KEY ?? '',
        SENTRY_DSN: process.env.SENTRY_DSN ?? '',
        APP_ENV: process.env.APP_ENV ?? '',
        GA_MEASUREMENT_ID: process.env.GA_MEASUREMENT_ID ?? '',
        COMMIT_HASH: process.env.COMMIT_HASH ?? '',
      }
    : null

const initGlobalsScript = [
  `window.GOOGLE_CLIENT_ID = window.GOOGLE_CLIENT_ID || ${JSON.stringify(publicEnv?.GOOGLE_CLIENT_ID ?? '')};`,
  `window.FACEBOOK_CLIENT_ID = window.FACEBOOK_CLIENT_ID || ${JSON.stringify(publicEnv?.FACEBOOK_CLIENT_ID ?? '')};`,
  `window.LINKEDIN_CLIENT_ID = window.LINKEDIN_CLIENT_ID || ${JSON.stringify(publicEnv?.LINKEDIN_CLIENT_ID ?? '')};`,
  `window.STRIPE_PUBLIC_KEY = window.STRIPE_PUBLIC_KEY || ${JSON.stringify(publicEnv?.STRIPE_PUBLIC_KEY ?? '')};`,
  `window.SENTRY_DSN = window.SENTRY_DSN || ${JSON.stringify(publicEnv?.SENTRY_DSN ?? '')};`,
  `window.APP_ENV = window.APP_ENV || ${JSON.stringify(publicEnv?.APP_ENV ?? '')};`,
  `window.GA_MEASUREMENT_ID = window.GA_MEASUREMENT_ID || ${JSON.stringify(publicEnv?.GA_MEASUREMENT_ID ?? '')};`,
  `window.COMMIT_HASH = window.COMMIT_HASH || ${JSON.stringify(publicEnv?.COMMIT_HASH ?? '')};`,
  // The CDN host for this deployment (resolved server-side from URL_CDN; '' = same-origin in dev/e2e)
  // plus globalThis.__assetUrl — the runtime resolver Vite's renderBuiltUrl form calls from JS chunks
  // (see vite.config.ts) and that lib/asset-url.ts mirrors. MUST be defined here, in the <head> script,
  // before <Scripts/> loads the deferred entry so the first chunk's asset URLs already resolve.
  `window.__CDN_URL = window.__CDN_URL || ${JSON.stringify(getRuntimeCdnUrl())};`,
  `globalThis.__assetUrl = globalThis.__assetUrl || function(f){var b=(window.__CDN_URL||'').replace(/\\/$/,'');return (b?b+'/':'/')+String(f).replace(/^\\//,'')};`,
].join('')

// Only 'dark'/'light' are explicit choices. Anything else — no cookie, or a stale 'theme=undefined'
// a previous build wrote for system mode — means follow the OS preference. (Treating a non-light
// value as "not system" used to strip the class for system-dark visitors → a light→dark flash.)
const manageThemeScript = `
function manageTheme() {
  var m = document.cookie.match(/(?:^|;\\s*)theme=([^;]*)/)
  var theme = m ? m[1] : ''
  var dark = theme === 'dark' || (theme !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}
manageTheme()
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', manageTheme)
`

// The render-critical font + favicon live in public/, so they bypass Vite and are resolved by hand via
// assetUrl() (see lib/asset-url.ts), which reads the same runtime CDN host as every other asset — so
// SSR and the client's first render produce identical markup (no hydration mismatch). We also preconnect
// to the CDN host (cdnOrigin) so its DNS+TCP+TLS handshake overlaps HTML parsing instead of blocking the
// first asset; cdnOrigin is '' when assets are same-origin (dev / e2e) and the preconnect is then
// skipped. The favicon is resolved in head(); the font URL + @font-face + preconnect render inside
// RootDocument, where the per-render cdnUrl is read.

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  locale: Locale
  // Request-scoped Lingui instance (see createLocaleI18n). The SSR render tree reads its translations
  // from this instance via <I18nProvider>, so concurrent requests in the same worker can't clobber each
  // other's active locale (React #418).
  i18n: I18n
}>()({
  validateSearch: rootSearchSchema,
  search: {
    middlewares: [retainSearchParams(['token', 'lang', 'clientId', 'ref']), stripSearchParams(defaultValues)],
  },
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.auth.getUser.queryOptions())
    context.queryClient.prefetchQuery(trpc.auth.getApp.queryOptions())
    context.queryClient.prefetchQuery(trpc.employees.getMyEmployers.queryOptions())

    // `theme` is resolved server-side from the `theme` cookie so SSR can paint the `dark` class on
    // <html> directly (no light→dark flash). Dehydrated into the client, so hydration reads the same
    // value and there's no <html> className mismatch. See lib/server-theme.
    // `locale` comes from the router context — resolved + activated once in `getRouter` (server: from
    // the request; client: from `<html lang>`), so there's no second resolve/catalog-load here. The
    // root route reads it to render `<html lang>`.
    return { theme: resolveServerTheme(), locale: context.locale }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'theme-color', content: '#33CCAA' },
      { title: 'D’Anchiano' },
      { name: 'description', content: 'La manera más fácil y rápida de evaluar talento' },
    ],
    // NOTE: app.css and the font preload are deliberately NOT here — they're plain <link>s in
    // RootDocument's <head>. A stylesheet emitted via head() gets a `precedence` prop, and React 19
    // hoists precedence stylesheets to the top of <head>, above our inline `manageTheme` script. A
    // parser-blocking inline script that follows a render-blocking stylesheet must wait for that
    // stylesheet to load, and the browser paints the (themeless) DOM during the wait → light→dark
    // flash for system-mode visitors (no `theme` cookie, so the server can't pre-paint the class).
    // Rendering them as plain links in JSX (after the script) keeps DOM order, so the script runs first.
    links: [{ rel: 'shortcut icon', href: assetUrl('favicon.ico') }],
    scripts: [{ children: initGlobalsScript }],
  }),
  component: RootComponent,
  notFoundComponent: () => {
    return (
      <RootDocument>
        <p>{'Not found :('}</p>
      </RootDocument>
    )
  },
})

// React Grab is a dev-only pointer-inspection overlay; load it lazily on the client. Kept as a
// module-level helper (not an inline import() inside the effect) so the React Compiler can optimize
// RootComponent — it bails out of a whole component that contains an import() expression. VITE_E2E
// disables it because the overlay otherwise intercepts pointer events on full-page views and inside
// menu/dialog portals during e2e runs.
function loadReactGrabDevOverlay() {
  if (import.meta.env.DEV && !import.meta.env.VITE_E2E) {
    void import('react-grab')
  }
}

function RootComponent() {
  const { theme, locale } = Route.useLoaderData()

  React.useEffect(() => {
    loadReactGrabDevOverlay()
  }, [])

  return (
    <RootDocument theme={theme} locale={locale}>
      <AppShell>
        <Outlet />
      </AppShell>
    </RootDocument>
  )
}

function RootDocument(
  props: Readonly<{ children: React.ReactNode; theme?: 'dark' | 'light' | null; locale?: string }>,
) {
  // Resolved per render so SSR (process.env.URL_CDN) and the client's first render (window.__CDN_URL,
  // set by the head script before hydration) agree byte-for-byte. cdnOrigin is '' when same-origin.
  const cdnUrl = getRuntimeCdnUrl()
  const cdnOrigin = cdnUrl.startsWith('http') ? new URL(cdnUrl).origin : ''
  const fontUrl = assetUrl('montserrat-latin-wght-normal.woff2')
  // Self-hosted Montserrat (variable weight axis), the `--font-primary` family. Declared inline in the
  // document <head> — co-located with the preload below — NOT in app.css, so the browser sees the
  // @font-face at initial HTML parse and matches it to the preloaded woff2 immediately (otherwise the
  // preload is "used but not within a few seconds" → Chrome warning, and text falls back to the
  // metric-matched `fallback-font` Verdana). `font-display: swap` + that fallback keep the swap shift-free.
  const fontFaceStyle = `@font-face{font-family:'Montserrat';font-style:normal;font-display:swap;font-weight:100 900;src:url('${fontUrl}') format('woff2')}`
  return (
    // `theme` (the `theme` cookie, resolved server-side in the loader) paints the dark class on the
    // first SSR render so there's no flash for explicit-cookie visitors. In system mode `theme` is null
    // (the server can't read the OS preference) and the inline manageTheme script adds `dark` before
    // paint instead. suppressHydrationWarning lets that script-set class stand through hydration; the
    // script + AppShell effect remain the live source of truth (system mode, OS changes, toggles).
    // `lang` is the server-resolved locale; the AppShell effect keeps it in sync after hydration.
    <html lang={props.locale} className={props.theme === 'dark' ? 'dark' : undefined} suppressHydrationWarning>
      <head>
        {/* Warm up the cross-origin CDN connection (DNS+TCP+TLS) before the parser reaches the asset
            URLs, so the handshake overlaps HTML parsing instead of blocking the first chunk/font.
            crossOrigin matches the anonymous CORS fetches the CDN serves (modules + font). Skipped
            when assets are same-origin (dev), where CDN_ORIGIN is ''. */}
        {cdnOrigin ? (
          <>
            <link rel="preconnect" href={cdnOrigin} crossOrigin="anonymous" />
            <link rel="dns-prefetch" href={cdnOrigin} />
          </>
        ) : null}
        {/* manageTheme MUST run before the stylesheet. It sets the `dark` class from the cookie /
            `prefers-color-scheme` so the first paint is correct even in system mode (no `theme`
            cookie → the server can't pre-paint the class on <html>). */}
        <script dangerouslySetInnerHTML={{ __html: manageThemeScript }} />
        {/* Plain (non-precedence) <link> so React 19 keeps it HERE in DOM order — after the script
            — instead of hoisting it above (which it does for precedence stylesheets emitted via
            head().links). Still render-blocking, so no flash of unstyled content. See head() note. */}
        <link rel="stylesheet" href={appCss} />
        <link rel="preload" href={fontUrl} as="font" type="font/woff2" crossOrigin="anonymous" />
        <style dangerouslySetInnerHTML={{ __html: fontFaceStyle }} />
        <HeadContent />
      </head>
      <body>
        {props.children}
        <Scripts />
      </body>
    </html>
  )
}

// Supplies the translated label for optional form fields. Without it, FieldLabelRow falls back to
// the hardcoded English "Optional" default in every locale. Lives under I18nProvider so `t` resolves.
function OptionalLabelProvider(props: Readonly<{ children: React.ReactNode }>) {
  const { t } = useLingui()
  return <FormOptionalLabelProvider value={t`Optional`}>{props.children}</FormOptionalLabelProvider>
}

// Sync the app/branding config into the store — replaces the old `window.APP` global. The config now
// comes from its OWN `getApp` query (keyed on the request's appId, not the user), so an embed/API
// visitor gets the client's branding even when logged out. Only fires in an app/embed context (`app`
// is undefined otherwise), so the standalone app keeps its defaults. setStore deep-merges customize.
function AppConfigSync() {
  const { data: app } = useSuspenseQuery(trpc.auth.getApp.queryOptions())
  const setStore = useAppStore((state) => state.setStore)

  React.useEffect(() => {
    if (!app) return
    setStore({
      app,
      // The per-app theme wins in app/SDK context: a boolean forces the mode (so a client can pin
      // its embed to light or dark); `null` leaves the visitor's own preference / system default.
      ...(typeof app.darkMode === 'boolean' ? { darkMode: app.darkMode ? 'dark' : 'light' } : {}),
      customize: {
        accentColor: app.accentColor ?? null,
        darkMode: app.darkMode ?? undefined,
        applicantButtonExit: app.applicantButtonExit ?? undefined,
        jobButtonExit: app.jobButtonExit ?? undefined,
        enableJobAddApplicantButton: app.enableJobAddApplicantButton,
      },
    })
  }, [app, setStore])

  return null
}

function AppUserSync() {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const routeTo = useAppStore((state) => state.store.routeTo)
  const profiles = useAppStore((state) => state.store.profiles)

  // Identity guard for the persisted (localStorage) query cache — see PERSISTED_IDENTITY_KEY in @/main.
  // The social-OAuth login path lands via a full document load that rehydrates the persisted blob for
  // the PREVIOUS account (only password login / logout run clearQueryClient), so a prior user's
  // identity-scoped queries (e.g. getMyEmployeeProfile) surfaced in the user menu. When getUser resolves
  // for a different id than the persisted tag, drop the whole cache and re-seed the authoritative user so
  // the identity-scoped queries refetch for the current account without a getUser round-trip.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const currentId = user?.id != null ? String(user.id) : 'anon'
    const previousId = window.localStorage.getItem(PERSISTED_IDENTITY_KEY)
    if (previousId !== null && previousId !== currentId) {
      clearQueryClient()
      queryClient.setQueryData(trpc.auth.getUser.queryKey(), user)
    }
    window.localStorage.setItem(PERSISTED_IDENTITY_KEY, currentId)
  }, [user, queryClient])

  React.useEffect(() => {
    if (routeTo) {
      // TODO: maintaining retrocompatibility with old sdk
      // TODO: Fix this, surely is not working correctly now with the new router
      navigate({
        to: routeTo,
        search: {
          ...profiles,
          second:
            profiles?.first && !profiles.second && user?.type === 'applicant'
              ? { type: 'applicant', id: user.id }
              : profiles?.second,
        },
        replace: true,
      })
    }
  }, [user, routeTo, profiles, navigate])

  return null
}

// Brand accent palette as a rendered <style>, NOT a post-mount effect. The per-app color comes from
// the SSR-prefetched `getApp` (useSuspenseQuery), so the rule is part of the server HTML and the
// accent paints with the first frame on a reload — no teal→custom flash, no localStorage. Reading it
// from `getApp` (not `getUser`) also means the brand accent paints for a logged-out embed/API visitor.
// A live SDK `customize` postMessage lands a non-null `customize.accentColor` in the store, which
// overrides the app value and re-renders this rule. `:root:root` wins over app.css's `:root`/`.dark`
// declarations (it's also emitted after the app.css <link>, so equal-specificity ties fall our way too).
function AccentTheme() {
  const { data: app } = useSuspenseQuery(trpc.auth.getApp.queryOptions())
  const overrideAccent = useAppStore((state) => state.store.customize.accentColor)
  const accent = overrideAccent ?? app?.accentColor ?? null
  const css = React.useMemo(() => {
    const body = Object.entries(accentThemeVars(accent))
      .map(([key, value]) => `${key}:${value}`)
      .join(';')
    return `:root:root{${body}}`
  }, [accent])
  return <style>{css}</style>
}

function AppShell(props: Readonly<{ children: React.ReactNode }>) {
  const queryClient = useQueryClient()
  // Request-scoped Lingui instance from router context — never the shared global — so concurrent SSR
  // renders can't clobber this request's locale (React #418). See createLocaleI18n.
  const { i18n } = Route.useRouteContext()
  /**
   * SDK
   * - run renders and requests from parent
   * - send response for requests made from parent
   * - recover last render props after app autoupdate (see Page props implementation)
   */
  const sdk = typeof window !== 'undefined' && window.self !== window.top
  const setStore = useAppStore((state) => state.setStore)
  const clientId = useAppStore((state) => state.store.clientId)
  const locale = useAppStore((state) => state.store.locale)
  const darkMode = useAppStore((state) => state.store.darkMode)
  const app = useAppStore((state) => state.store.app)

  React.useEffect(() => {
    const listener = (
      event: MessageEvent<{
        type: string
        props?: Record<string, unknown>
        url?: string
        params?: unknown
        key?: string
      }>,
    ) => {
      if (event.data.type === 'danchiano_render') {
        setStore({
          ...event.data.props,
          sdk,
          callback: (value) => {
            if (!sdk) return
            window.parent.postMessage({ type: 'danchiano_callback', result: value }, '*')
          },
        })
      } else if (event.data.type === 'danchiano_request') {
        if (!event.data.url || !event.data.params) return

        // TODO: check if we need all of this flow, we should add headers and everything here
        fetch(event.data.url, event.data.params)
          .then((result) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result,
              },
              '*',
            )
          })
          .catch((error: unknown) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result: (error as { response?: string }).response ?? error,
              },
              '*',
            )
          })
      }
    }

    window.addEventListener('message', listener, false)
    window.parent.postMessage({ type: 'danchiano_loaded' }, '*')
    return () => window.removeEventListener('message', listener, false)
  }, [sdk, setStore])

  // SDK: auto resize iframe on content change
  React.useEffect(() => {
    if (!sdk) return

    const target = document.getElementById('root-page')
    if (target) {
      const sendHeight = () => {
        window.parent.postMessage(
          {
            type: 'danchiano_resize',
            height: `${target.offsetHeight}px`,
          },
          '*',
        )
      }
      sendHeight()
      const observer = new ResizeObserver(sendHeight)
      observer.observe(target)
    }
  }, [sdk])

  const setHydrated = useAppStore((state) => state.setHydrated)
  React.useEffect(() => {
    // Record that client React has mounted. SSR'd interactive controls read this (via useHydrated) to
    // stay disabled until handlers are attached, so pre-hydration clicks aren't silently dropped.
    setHydrated()
  }, [setHydrated])

  React.useEffect(() => {
    // Keep <html lang> in sync with the active locale so assistive tech announces content in the
    // right language. The SSR value comes from the loader; this owns it after hydration / on changes.
    if (locale) document.documentElement.lang = locale
  }, [locale])

  const prevScope = React.useRef<string | null>(null)
  React.useEffect(() => {
    // Invalidate ONLY when clientId/locale actually CHANGE value (SDK switches tenant, user changes
    // language) — then the cached data really is for the wrong scope. A "skip first run" boolean
    // guard isn't enough: this effect re-runs with the SAME values (e.g. after getUser resolves and
    // AppShell re-renders), and invalidating then throws away the fresh SSR-hydrated cache and
    // refetches everything. Comparing the previous value avoids that spurious invalidation.
    const scope = `${clientId ?? ''}|${locale ?? ''}`
    if (prevScope.current !== null && prevScope.current !== scope) {
      queryClient.invalidateQueries()
    }
    prevScope.current = scope
  }, [clientId, locale, queryClient])

  const [, setCookie, removeCookie] = useCookies(['theme'])

  // The visitor's `theme` cookie is their PERSONAL standalone-app preference. In any embed/API context
  // — the SDK iframe (`sdk`) OR an API token-link app (full page, so `sdk` is false but `app` is set) —
  // the dark mode is dictated by the client's app config, so it must NOT overwrite that personal cookie.
  const isEmbedded = sdk || !!app

  React.useEffect(() => {
    if (darkMode === 'dark') {
      if (!isEmbedded) setCookie('theme', 'dark', { path: '/' })

      document.documentElement.classList.add('dark')
      setStore({ resolvedDarkMode: 'dark' })
    } else if (darkMode === 'light') {
      if (!isEmbedded) setCookie('theme', 'light', { path: '/' })

      document.documentElement.classList.remove('dark')
      setStore({ resolvedDarkMode: 'light' })
    } else {
      if (!isEmbedded) removeCookie('theme', { path: '/' })

      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark')
        setStore({ resolvedDarkMode: 'dark' })
      } else {
        document.documentElement.classList.remove('dark')
        setStore({ resolvedDarkMode: 'light' })
      }
    }
  }, [darkMode, isEmbedded, setCookie, removeCookie, setStore])

  // NOTE (intentional, not pending): auto-reload-on-new-version is deliberately left disabled —
  // during intermediate deployment states it could cause constant reloads, hurting UX.
  // // Reload when a new version is available
  // const newVersionAvailable = useAppStore(state => state.newVersionAvailable)
  // useLayoutEffect(() => {
  //   if (newVersionAvailable) {
  //     window.location.reload()
  //   }
  // }, [pathname])

  return (
    <div id="root-page" className="relative mx-auto">
      {/* <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}> */}
      <I18nProvider i18n={i18n}>
        <TooltipProvider delayDuration={30}>
          <OptionalLabelProvider>
            <React.Suspense fallback={null}>
              <AppConfigSync />
              <AppUserSync />
              <AccentTheme />
            </React.Suspense>
            {props.children}
            <ClientOnly fallback={null}>
              <CookieBanner />
            </ClientOnly>
          </OptionalLabelProvider>
        </TooltipProvider>
        <OnDemandToaster />
      </I18nProvider>
      {/* </Sentry.ErrorBoundary> */}

      {/* Off under VITE_E2E: its server vite plugin (the console-pipe) is disabled in
          the e2e stack, so the panel's /__tsd/* calls would 404; and it's pure dev UI
          irrelevant to headless tests. */}
      {import.meta.env.DEV && !import.meta.env.VITE_E2E && (
        <ClientOnly fallback={null}>
          <TanStackDevtools
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
              {
                name: 'Tanstack Query',
                render: <ReactQueryDevtoolsPanel />,
              },
            ]}
          />
        </ClientOnly>
      )}
    </div>
  )
}
